<template>
  <div class="text-center pb-0 px-4 sm:px-6 lg:px-8">
    <div class="mb-6 flex flex-row-reverse">
      <SearchInput placeholder="Search by Email / Name" v-model="searchKeyword" @input="fetchUsersOnSearch" />
    </div>
    <div class="overflow-x-auto rounded-lg bg-white shadow">
      <div class="align-middle inline-block w-full">
        <table class="divide-gray-300 w-full text-left table-auto">
          <thead class="border-b-2">
            <tr>
              <th
                scope="col"
                class="px-6 py-5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                <FilterableHeaderCell
                  title="Email"
                  param="email"
                  @switchOrder="onSwitchOrder"
                />
              </th>
              <th
                scope="col"
                class="px-6 py-5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                <FilterableHeaderCell
                  title="Name"
                  param="name"
                  @switchOrder="onSwitchOrder"
                />
              </th>
              <th
                scope="col"
                class="px-6 py-5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                <FilterableHeaderCell
                  title="Created At"
                  param="created_at"
                  @switchOrder="onSwitchOrder"
                />
              </th>
              <th
                scope="col"
                class="px-6 py-5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                <FilterableHeaderCell
                  title="Deleted At"
                  param="deleted_at"
                  @switchOrder="onSwitchOrder"
                />
              </th>
              <th
                scope="col"
                class="px-6 py-5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Status
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="user in users" :key="user.id">
              <td
                @click="toUserDetails(user)"
                class="py-5 px-6 text-sm text-gray-500 hover:bg-gray-50 cursor-pointer"
              >
                {{ user.email }}
              </td>
              <td
                @click="toUserDetails(user)"
                class="py-5 px-6 text-sm text-gray-500 hover:bg-gray-50 cursor-pointer"
              >
                {{ user.name }}
              </td>
              <td
                class="py-5 px-6 text-sm text-gray-500"
              >
                {{ formattedDate(user.created_at) }}
              </td>
              <td
                class="py-5 px-6 text-sm text-gray-500"
              >
                {{ formattedDate(user.deleted_at) }}
              </td>
              <td
                class="py-5 px-6 text-sm text-gray-500"
              >
                {{ updateStatus(user) }}

              </td>
              <td class="py-5 px-6">
                <div v-if="!user.verified_email_at && !user.deleted_at && !user.blocked_at">
                  <button
                  type="button"
                  class="button-submit w-32"
                  @click="verifyUser(user)"
                >
                  Verify User
                </button>
                </div>

                <div v-else-if="user.blocked_at">
                  <button
                  type="button"
                  class="button-cancel w-32"
                  @click="adminUnBlockUser(user)"
                >
                  Unblock User
                </button>
                </div>

                <div v-else-if="user.verified_email_at && !user.deleted_at && !user.blocked_at">
                  <button
                  type="button"
                  class="button-submit w-32"
                  @click="adminBlockUser(user)"
                >
                  Block User
                </button>
                </div>

                <button
                  v-if="user.data_purged_at"
                  type="button"
                  class="button-cancel w-32 mt-2"
                  @click="viewErasureRecord(user)"
                >
                  View record
                </button>
              </td>
            </tr>
          </tbody>
        </table>
        <Paginator
          ref="paginator"
          :onPageChange="onPageChange"
          :data="paginationData"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { ref } from "vue";
import Paginator from "@/components/common/Paginator";
import FilterableHeaderCell from "@/components/common/FilterableHeaderCell";
import SearchInput from "@/components/common/SearchInput";
import useConfirmationModal from "@/api/confirmationModal";
import eventBus from "@/utils/eventBus";
import { ERASURE_RECORD_EVENT } from "@/components/admin/user-page/modals/erasureEvents";

export default {
  name: "UserList",
  components: {
    Paginator,
    FilterableHeaderCell,
    SearchInput,
  },
  async beforeMount() {
    await this.fetchUsers();
  },
  setup() {
    return {
      users: ref([]),
      paginationData: ref([]),
      searchKeyword: ref(""),
      ordering: ref("-created_at"),
      page: ref(1),
    };
  },
  methods: {
    formattedDate(timezone) {
      if(timezone){
        return new Date(timezone).toLocaleString("en-US");
      }else{
        return null;
      }
    },
    updateStatus(user){
      // data_purged_at first: an erasure also sets deleted_at, so checking
      // deleted_at ahead of it would hide erasure entirely.
      if(user.data_purged_at){
        return "Erased"
      }else if(user.deleted_at){
        return "Deleted"
      }else if(user.blocked_at){
        return "Blocked"
      }
      else if (user.verified_email_at){
        return "Verified"
      }else{
        return "Not Verified"
      }
    },
    viewErasureRecord(user){
      eventBus.trigger(ERASURE_RECORD_EVENT, {
        userId: user.id,
        email: user.email,
      });
    },
    async fetchUsers() {
      let urlParams = {
        ordering: this.ordering,
        page: this.page,
        search: this.searchKeyword,
      };

      const users = await this.$alfredService.getUsers(urlParams);
      const { results, ...paginationData } = users;
      this.users = results;
      this.paginationData = paginationData;
    },
    async fetchUsersOnSearch() {
      await this.fetchUsers();
      this.page = 1;
      this.$refs.paginator.page = 1;
    },
    async verifyUser(user) {
      const modal = useConfirmationModal();
      await modal.openModal({
        title: "Are you sure you want to confirm?",
        onSubmit: async () => {
          await this.$alfredService.adminVerifyUser(user);
          eventBus.trigger("notify", {
            notification: {
              group: "top-right",
              type: "success",
              text: "User Successfully Verified!",
            },
          });
          await this.fetchUsers();
        },
      });
    },
    async adminBlockUser(user){
      let payload = {
        user_id: user.id,
        action: "block"
      }
      const modal = useConfirmationModal();
      await modal.openModal({
        title: "Are you sure you want to confirm?",
        onSubmit: async () => {
          await this.$alfredService.adminUserBlockActions(payload);
          eventBus.trigger("notify", {
            notification: {
              group: "top-right",
              type: "success",
              text: "User Successfully Blocked!",
            },
          });
          await this.fetchUsers();
        },
      });
    },
    async adminUnBlockUser(user){
      let payload = {
        user_id: user.id,
        action: "unblock"
      }
      const modal = useConfirmationModal();
      await modal.openModal({
        title: "Are you sure you want to confirm?",
        onSubmit: async () => {
          await this.$alfredService.adminUserBlockActions(payload);
          eventBus.trigger("notify", {
            notification: {
              group: "top-right",
              type: "success",
              text: "User Successfully Unblocked!",
            },
          });
          await this.fetchUsers();
        },
      });
    },
    async onPageChange(page) {
      this.page = page;
      await this.fetchUsers();
    },
    async onSwitchOrder(ordering) {
      this.ordering = !ordering ? "-created_at" : ordering;
      await this.fetchUsers();
      this.$refs.paginator.setPage(1);
    },
    async toUserDetails({ id }) {
      await this.$router.push({
        name: "UserDetails",
        params: { userId: id },
      });
    },
  },
};
</script>
